import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import {
  // useAddSponsorUser, // COMMENTED OUT - Dual Role
  useCreateProviderUser,
  useGetAllProviderUsersByProviderId,
  useGetAllSponsorUserList,
  useGetprovidersListForFilters,
  useGetProviderOrgDetails,
  // useGetAllSponsorsForAdmin, // COMMENTED OUT - Dual Role
  useGetListForProviderUserDetails,
  // useGetSponsorUserDetails, // COMMENTED OUT - Dual Role
  useUpdateProviderUsers,
  // useUpdateSponsorUser, // COMMENTED OUT - Dual Role
} from "../../../custom-hooks/queries";
import NotificationModal from "../../../components/NotificationModal";
import { v4 as uuidv4 } from "uuid";
import FormSkeletonLoader from "../../../components/FormSkeletonLoader";
import MfaReadOnlyField from "../../../components/MfaReadOnlyField";
import {
  rightsDescriptions,
  vendorRightsList,
  roleDefinitions,
  vendorRightsCategories,
  vendorRoleDefinitions,
  rightsList,
  RIGHTS_DISPLAY_NAMES,
  ROLE_DISPLAY_NAMES,
  PERMISSION_CATEGORY_DISPLAY_NAMES,
} from "../../../utils/constants";
import ComboBox from "../../../components/ComboBox";
import ExistingUserSelector from "./ExistingUserSelector";
import { useUserRole } from "../../../utils/helperMethods";
import ContactDesignationSection from "../../../components/ContactDesignationSection";
import {
  isSameClerkId,
  getVendorSelfProfileRedirectPath,
  getVendorUserFormCopy,
} from "../../../utils/selfProfileRedirect";
import {
  getContactDesignationFromRow,
  getEligibleCategoriesFromSubRoles,
} from "../../../utils/requestContactDesignations";
import { applyContactDesignations } from "../../../utils/applyContactDesignations";
import { getAllProviderUsersByProviderId } from "../../../api/api-methods";
import { supportedEmailCharacters } from "../../../utils/emailSupportedCharacters";
import {
  DEFAULT_NEW_VENDOR_SUBROLES,
  DEFAULT_VENDOR_REQUEST_CONTACT_DESIGNATION,
  applyVendorPermissionsForRoles,
  isUserRoleSelectionSaveDisabled,
  normalizeFormRoleSelection,
} from "../../../utils/userRolePermissionDefaults";
import { getUserSubRoleNames } from "../../../utils/overviewContactsShared";

/** Maps ExistingUserSelector vendor-role dropdown ids (type=sponsor) to form role names */
const EXISTING_SPONSOR_ROLE_ID_TO_VENDOR_ROLE = {
  admin: "Vendor Admin",
  "rfi-manager-approver": "RFI Manager - Approver",
  "rfi-manager-user": "RFI Manager - User",
  "audit-manager": "VQA Manager",
};

/** Reusable text input component */
const FormInput = ({
  label,
  name,
  register,
  errors,
  placeholder,
  numeric,
  type = "text",
  options,
  disabled = false,
  className = "",
  setValue,
  watch,
}) => {
  if (type === "select") {
    return (
      <div className={`flex flex-col ${className}`}>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          {label}
        </label>
        <ComboBox
          data={options?.items}
          placeHolder="Select Vendors"
          setSelected={(value) => setValue(name, value)}
          selected={watch(name)}
          width={"w-[700px]"}
          inputWidth={"w-[620px]"}
          disabled={disabled}
        />
        {errors[name] && (
          <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <input
        type={type}
        {...register(name)}
        placeholder={placeholder}
        disabled={disabled}
        onInput={
          name === "phone_number" || name === "office_phone_number"
            ? (e) => {
                let value = e.target.value.replace(/[^\d]/g, "");
                if (value.length > 0) {
                  value = value.replace(
                    /^(\d{1})(\d{0,3})(\d{0,3})(\d{0,4}).*/,
                    (m, g1, g2, g3, g4) => {
                      let out = g1;
                      if (g2) out += "-" + g2;
                      if (g3) out += "-" + g3;
                      if (g4) out += "-" + g4;
                      return out;
                    }
                  );
                }
                // Limit to X-XXX-XXX-XXXX format
                if (value.length > 14) {
                  value = value.slice(0, 14);
                }
                e.target.value = value;
              }
            : undefined
        }
        maxLength={
          name === "phone_number" || name === "office_phone_number"
            ? 14
            : undefined
        }
        className={`mt-1 w-full border-gray-300 rounded-md text-sm ${
          disabled ? "bg-gray-100" : ""
        }`}
      />
      {errors[name] && (
        <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
      )}
    </div>
  );
};

/** Reusable radio group component for contact preference */
const RadioGroup = ({ label, name, register, errors }) => (
  <div className="flex flex-col">
    <label className="block text-sm font-medium text-gray-900">{label}</label>
    <div className="flex items-center gap-6 mt-1">
      <label className="flex items-center">
        <input
          type="radio"
          {...register(name)}
          value="Mobile_Phone"
          className="mr-2"
        />
        Mobile Phone
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          {...register(name)}
          value="Office_Phone"
          className="mr-2"
        />
        Office Phone
      </label>
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
    )}
  </div>
);

const resolveOrgIdFromParam = (rawOrgId) => {
  if (rawOrgId == null || rawOrgId === "") return null;
  const asNumber = Number(rawOrgId);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
  try {
    const decodedNum = Number(atob(rawOrgId));
    if (Number.isFinite(decodedNum) && decodedNum > 0) return decodedNum;
  } catch {
    // not base64-encoded
  }
  return null;
};

const AddProviderUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const providerName = searchParams.get("providerName");
  const providerIdParam = searchParams.get("providerId");
  const clerkId = searchParams.get("clerkId");
  const orgId = searchParams.get("orgId");
  const user = useUserRole();
  const { user: clerkUser } = useUser();
  const isVendorSelfService = user === "Provider User";
  const isEditingSelf = isSameClerkId(clerkId, clerkUser?.id);
  // Ticket 14428: never allow self role/permission edits. Editing others stays
  // as before — Vendor Admin route + Diligent admin backend routes already gate
  // access; Identity also enforces role-change rules server-side.
  const rolesLocked = isEditingSelf;

  useEffect(() => {
    if (!isEditingSelf) return;
    const path = getVendorSelfProfileRedirectPath(user);
    if (path) navigate(path, { replace: true });
  }, [isEditingSelf, navigate, user]);

  // API calls
  // Lightweight cached vendor list ({id, provider_id, name, email, hc_org_id,
  // is_active, discovery_status}, name-sorted server-side) instead of paging
  // the full provider entities through GET /Providers (14465).
  const { data: providerOrgList, isLoading: isLoadingProviderOrg } =
    useGetprovidersListForFilters();
  const providerOrgData = useMemo(
    () => (providerOrgList ? { items: providerOrgList } : undefined),
    [providerOrgList]
  );

  const { data: vendorOrgDetails } = useGetProviderOrgDetails(
    isVendorSelfService && providerIdParam ? providerIdParam : null
  );

  // Track which user's data has been loaded into the form
  const loadedUserIdRef = useRef(null);

  // Use a timestamp to force cache invalidation
  const [refreshKey, setRefreshKey] = useState(Date.now());

  // Improve the query with better options

  const {
    data: vendorUserDetails,
    isLoading: vendorUserDetailsLoading,
    refetch,
  } = useGetListForProviderUserDetails(userId);

  const getVendorTeamProfilePath = (fallbackProviderId) => {
    const vendorId =
      fallbackProviderId ||
      providerIdParam ||
      vendorOrgDetails?.provider_id ||
      vendorUserDetails?.provider_id;
    if (!vendorId) {
      return "/user/team/profile/edit?tab=users";
    }
    return `/user/team/profile/edit?vendorId=${encodeURIComponent(vendorId)}&tab=users`;
  };


  // Add this effect to force refetch on mount
  useEffect(() => {
    if (userId) {
      // Force invalidate cache and refetch when component mounts
      setRefreshKey(Date.now());
      refetch();

      // Create a timer to check if data loaded properly after 2 seconds
      const timer = setTimeout(() => {
        refetch();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [userId]);

  const { mutate: addProviderUser } = useCreateProviderUser();
  const { mutate: updateProviders } = useUpdateProviderUsers();
  // const { mutate: addSponsorUser } = useAddSponsorUser(); // COMMENTED OUT - Dual Role

  const [alertOpen, setAlertOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [isSuccess, setIsSuccess] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [userDataLoaded, setUserDataLoaded] = React.useState(false);

  // Dual role state - COMMENTED OUT
  // const [enableDualRole, setEnableDualRole] = useState(false);
  // const [selectedSponsorRoles, setSelectedSponsorRoles] = useState([]);

  // Update schema to include is_active field when editing
  const schema = yup.object().shape({
    provider_id: userId
      ? yup.object().nullable()
      : yup.object().required("Select a Vendor Organization"),
    email: yup
      .string()
      .email("Enter a valid email")
      .test(supportedEmailCharacters)
      .required("Email is required"),
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Second name is required"),
    job_title: yup
      .string()
      .nullable()
      .transform((value) => (value === "" || value === undefined ? null : value)),
    office_phone_number: yup
      .string()
      .matches(
        /^\d{1}-\d{3}-\d{3}-\d{4}$/,
        "Please enter a valid office phone number (e.g. X-XXX-XXX-XXXX)"
      )
      .nullable()
      .transform((value) => (value === "" ? null : value)),
    phone_number: yup
      .string()
      .matches(
        /^\d{1}-\d{3}-\d{3}-\d{4}$/,
        "Please enter a valid mobile phone number (e.g. X-XXX-XXX-XXXX)"
      )
      .nullable()
      .transform((value) => (value === "" ? null : value)),
    contact_preference: yup.string().required("Contact preference is required"),
    role: yup.array().min(1, "At least one role must be selected"),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: {
      contact_preference: "Mobile_Phone",
      role: DEFAULT_NEW_VENDOR_SUBROLES,
      is_active: true,
      job_title: "",
    },
  });

  const roleFieldValue = watch("role");
  const selectedRoles = useMemo(
    () => normalizeFormRoleSelection(roleFieldValue),
    [roleFieldValue],
  );

  useEffect(() => {
    if (providerOrgData?.items?.length > 0) {
      const selectedProvider = providerOrgData.items.find(
        (p) =>
          (providerIdParam && p.provider_id === providerIdParam) ||
          (providerName && p.name === providerName)
      );
      if (selectedProvider) {
        setValue("provider_id", selectedProvider);
      }
    }
  }, [providerOrgData, providerName, providerIdParam, setValue]);

  useEffect(() => {
    if (vendorOrgDetails && providerIdParam) {
      setValue("provider_id", vendorOrgDetails);
    }
  }, [vendorOrgDetails, providerIdParam, setValue]);

  // Create a state to track if roles have been manually changed after initial load
  const [rolesManuallyChanged, setRolesManuallyChanged] = useState(false);
  const [rfiContactDesignation, setRfiContactDesignation] = useState(() =>
    userId ? "none" : DEFAULT_VENDOR_REQUEST_CONTACT_DESIGNATION,
  );
  const [assessmentContactDesignation, setAssessmentContactDesignation] = useState(() =>
    userId ? "none" : DEFAULT_VENDOR_REQUEST_CONTACT_DESIGNATION,
  );

  // Load user data if in edit mode and data is available
  useEffect(() => {
    // Only load user data if we're in edit mode (userId exists)
    // AND we have user details AND the data hasn't been loaded yet
    if (
      userId &&
      vendorUserDetails &&
      loadedUserIdRef.current !== userId
    ) {
      loadedUserIdRef.current = userId;

      // Step 0: Clear the entire form first
      reset({}, { keepDefaultValues: false });

      // Step 1: Wait a tick for React to process the reset
      setTimeout(() => {
        // Step 2: Set the basic user information
        reset({
          // provider_id: vendorUserDetails.provider_id,
          email: vendorUserDetails.email,
          first_name: vendorUserDetails.first_name,
          last_name: vendorUserDetails.last_name,
          job_title:
            vendorUserDetails.job_title ??
            vendorUserDetails.title ??
            vendorUserDetails.jobTitle ??
            vendorUserDetails.JobTitle ??
            "",
          phone_number: vendorUserDetails.phone_number,
          office_phone_number: vendorUserDetails.office_phone_number,
          contact_preference:
            vendorUserDetails.contact_preference || "Mobile_Phone",
          is_active: vendorUserDetails.is_active !== false, // Default to true if not specified
          country: vendorUserDetails.country || "",
          role: [], // We'll set this separately
        });

        // Step 3: Reset all permissions to false first
        vendorRightsList.forEach((right) => {
          setValue(right, false);
        });

        // Step 4: Set roles with another small delay
        setTimeout(() => {
          const rolesFromApi = getUserSubRoleNames(vendorUserDetails);
          if (rolesFromApi.length > 0) {
            setValue("role", rolesFromApi);
          }
          // Step 5: Final step - explicitly set permissions from API
          setTimeout(() => {
            if (
              vendorUserDetails.permissions &&
              vendorUserDetails.permissions.length > 0
            ) {
              vendorUserDetails.permissions.forEach((right) => {
                if (right) {
                  setValue(right, true, { shouldValidate: true });
                }
              });
            }

            // Mark that we've loaded the initial user data
            setUserDataLoaded(true);
            setRfiContactDesignation(
              getContactDesignationFromRow(vendorUserDetails, "rfi"),
            );
            setAssessmentContactDesignation(
              getContactDesignationFromRow(vendorUserDetails, "assessment"),
            );
          }, 100);
        }, 100);
      }, 100);
    } else if (!userId) {
      reset({
        contact_preference: "Mobile_Phone",
        role: DEFAULT_NEW_VENDOR_SUBROLES,
        is_active: true,
        job_title: "",
        provider_id: getValues("provider_id") || undefined,
      });

      applyVendorPermissionsForRoles(setValue, DEFAULT_NEW_VENDOR_SUBROLES);
      setRfiContactDesignation(DEFAULT_VENDOR_REQUEST_CONTACT_DESIGNATION);
      setAssessmentContactDesignation(DEFAULT_VENDOR_REQUEST_CONTACT_DESIGNATION);
    }
  }, [
    vendorUserDetails,
    reset,
    setValue,
    getValues,
    userId,
  ]);

  const selectedProvider = watch("provider_id");

  const resolvedProviderId = providerIdParam || selectedProvider?.provider_id;
  const { data: vendorUsersData } = useGetAllProviderUsersByProviderId(
    resolvedProviderId,
    100,
    1,
    ""
  );
  const isSoleUserInOrg = (vendorUsersData?.items?.length ?? 0) <= 1;
  const contactDesignationsReadOnly = isEditingSelf && isSoleUserInOrg;
  // The filters list doesn't carry isdual_role (it would cost a cross-service
  // fan-out); derive it from the org's own users like AddSponsorUsers does.
  const vendorOrgUserRows = Array.isArray(vendorUsersData?.items)
    ? vendorUsersData.items
    : [];
  const orgIsDualRole = vendorOrgUserRows.some((u) => u.isdual_role);

  // Carry-over candidates are the SPONSOR-side users of the linked org.
  // Dual-role orgs created by this product share one GUID across
  // Sponsors.Id and Providers.provider_id, so the vendor org id addresses
  // the sponsor-side list directly (an org linked any other way returns an
  // empty list and the panel stays hidden).
  const { data: linkedSponsorUsersData } = useGetAllSponsorUserList({
    currentPage: 1,
    id: orgIsDualRole ? resolvedProviderId : null,
    search_text: "",
    page_size: 1000,
  });
  // A candidate whose email already exists among this vendor org's own users
  // must not be offered again (14464); isdual_role is not reliable for this.
  const existingVendorEmails = new Set(
    (vendorUsersData?.items ?? [])
      .map((u) => u.email?.toLowerCase())
      .filter(Boolean)
  );
  const availableSponsorUsers = (linkedSponsorUsersData?.items ?? [])
    .filter((u) => u.email && !existingVendorEmails.has(u.email.toLowerCase()))
    .map((u) => ({
      id: u.id,
      email: u.email,
      first_name: u.first_name,
      last_name: u.last_name,
      job_title: u.title,
      title: u.title,
      phone_number: u.phone_number,
      office_phone_number: u.office_phone_number,
      contact_preference: u.contact_preference,
      isdual_role: u.isdual_role,
    }));

  const syncVendorPermissionsAfterRoleChange = (newRoles) => {
    setTimeout(() => {
      setRolesManuallyChanged(true);
      applyVendorPermissionsForRoles(setValue, newRoles, { shouldDirty: true });
    }, 100);
  };

  useEffect(() => {
    if (userId) return;
    applyVendorPermissionsForRoles(setValue, selectedRoles, { shouldDirty: true });
  }, [selectedRoles, setValue, userId]);

  // Handle role selection with delayed permission updates
  const handleRoleChange = (e, role) => {
    if (rolesLocked) return;
    const isChecked = e.target.checked;

    const currentRoles = normalizeFormRoleSelection(getValues("role"));
    let newRoles = [];

    if (isChecked) {
      if (role === "Vendor Admin") {
        newRoles = ["Vendor Admin"];
      } else if (currentRoles.includes("Vendor Admin")) {
        newRoles = [role];
      } else {
        newRoles = [...currentRoles, role];
      }
    } else {
      newRoles = currentRoles.filter((r) => r !== role);
    }

    setValue("role", newRoles);
    syncVendorPermissionsAfterRoleChange(newRoles);
  };

  // Function to check if a role should be disabled
  const isRoleDisabled = (role) => {
    if (rolesLocked) return true;
    const currentRoles = normalizeFormRoleSelection(getValues("role"));
    if (role === "Vendor Admin") {
      // Disable Vendor Admin if any other role is selected
      return currentRoles.some((r) => r !== "Vendor Admin" && r !== role);
    } else {
      // Disable other roles if Vendor Admin is selected
      return currentRoles.includes("Vendor Admin");
    }
  };

  // Get sponsor permissions based on selected sponsor roles - COMMENTED OUT (Dual Role)
  // const getSponsorPermissionsForRoles = (sponsorRoles) => {
  //   const permissions = new Set();

  //   sponsorRoles.forEach((role) => {
  //     if (role === "Sponsor Admin") {
  //       rightsList.forEach((right) => {
  //         if (right !== "Trial Experience Dashboard") {
  //           permissions.add(right);
  //         }
  //       });
  //     } else if (role === "RFI Manager") {
  //       const rfiPermissions = [
  //         "View Requested RFIs",
  //         "Request RFIs",
  //         "Compare RFI",
  //         "View Released RFIs",
  //         "My Vendors",
  //       ];
  //       rfiPermissions.forEach((p) => {
  //         if (rightsList.includes(p)) permissions.add(p);
  //       });
  //     } else if (role === "VQA Manager") {
  //       const vqaPermissions = [
  //         "View and Request Library VQAs",
  //         "View Requested VQAs",
  //       ];
  //       vqaPermissions.forEach((p) => {
  //         if (rightsList.includes(p)) permissions.add(p);
  //       });
  //     }
  //   });

  //   return Array.from(permissions);
  // };

  // Handle sponsor role selection for dual role - COMMENTED OUT (Dual Role)
  // const handleSponsorRoleChange = (e, role) => {
  //   const isChecked = e.target.checked;
  //   let newRoles = [];

  //   if (isChecked) {
  //     if (role === "Sponsor Admin") {
  //       newRoles = ["Sponsor Admin"];
  //     } else if (selectedSponsorRoles.includes("Sponsor Admin")) {
  //       newRoles = [role];
  //     } else {
  //       newRoles = [...selectedSponsorRoles, role];
  //     }
  //   } else {
  //     newRoles = selectedSponsorRoles.filter((r) => r !== role);
  //   }

  //   setSelectedSponsorRoles(newRoles);
  // };

  // Check if sponsor role should be disabled - COMMENTED OUT (Dual Role)
  // const isSponsorRoleDisabled = (role) => {
  //   if (role === "Sponsor Admin") {
  //     return selectedSponsorRoles.some((r) => r !== "Sponsor Admin");
  //   } else {
  //     return selectedSponsorRoles.includes("Sponsor Admin");
  //   }
  // };


  // Modified onSubmit function to handle both create and update
  const onSubmit = (data) => {
    setIsLoading(true);

    // Ensure we have selected roles
    if (!selectedRoles || selectedRoles.length === 0) {
      setIsSuccess(false);
      setMessage("Please select at least one role");
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }

    // Get the vendor organization name from the selected vendor ID
    // const selectedVendor = providerOrgData?.find(
    //   (s) => s.provider_id === data.provider_id
    // );
    const targetProviderId =
      data?.provider_id?.provider_id || providerIdParam || vendorOrgDetails?.provider_id;
    const vendorName =
      data?.provider_id?.name || providerName || vendorOrgDetails?.name || "";
    const hyperComplyOrgId =
      resolveOrgIdFromParam(orgId) ?? data?.provider_id?.hc_org_id ?? null;
    if (!userId && (!hyperComplyOrgId || hyperComplyOrgId <= 0)) {
      setIsSuccess(false);
      setMessage(
        "Could not resolve organization for the selected vendor. Please reselect it from the list."
      );
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }

    // Get only the rights that are checked
    const rights = vendorRightsList.filter((right) => data[right] === true);

    // Prepare roles as array
    const rolesArray = Array.isArray(selectedRoles)
      ? selectedRoles
      : [selectedRoles];

    const eligibleCategories = getEligibleCategoriesFromSubRoles(rolesArray);

    const saveDesignations = async (provider_user_id, provider_id, email) => {
      if (contactDesignationsReadOnly) return;
      if (!provider_id || eligibleCategories.length === 0) return;

      const designationPayload = (resolvedUserId) => ({
        provider_user_id: resolvedUserId,
        provider_id,
        rfiDesignation: rfiContactDesignation,
        assessmentDesignation: assessmentContactDesignation,
        eligibleCategories,
      });

      try {
        await applyContactDesignations(designationPayload(provider_user_id));
      } catch (err) {
        const isNotFound = err.response?.status === 404;
        if (isNotFound && email) {
          try {
            const usersData = await getAllProviderUsersByProviderId(provider_id, 100, 1, "");
            const existing = usersData?.items?.find(
              (u) => u.email?.toLowerCase() === email.toLowerCase(),
            );
            if (existing?.provider_user_id) {
              await applyContactDesignations(designationPayload(existing.provider_user_id));
              return;
            }
          } catch (retryErr) {
            console.error("Failed to resolve provider user for contact designations", retryErr);
          }
        }
        console.error("Failed to save contact designations", err);
      }
    };

    const providerUserId = uuidv4();
    // Common data for both create and update
    const providerData = {
      email: data.email,
      provider_user_id: providerUserId, // Use existing ID or generate new one
      provider_id: targetProviderId,
      phone_number: data.phone_number,
      office_phone_number: data.office_phone_number,
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: (data.job_title ?? "").trim() || null,
      provider_name: vendorName,
      organization_id: hyperComplyOrgId,

      // Generate provider_subrole_dto with unique IDs
      provider_user_subrole: rolesArray.map((role) => ({
        // SubroleId: uuidv4(),
        Subrole: role,
      })),

      // Generate provider_rights_dto with unique IDs
      provider_user_permissions: rights.map((right) => ({
        // PermissionId: uuidv4(),
        Permission: right,
      })),
      is_active: selectedProvider?.is_active ?? true,
    };
    const updateProviderData = {
      email: data.email,
      provider_user_id: providerUserId, // Use existing ID or generate new one
      provider_id: vendorUserDetails?.provider_id,
      phone_number: data.phone_number,
      office_phone_number: data.office_phone_number,
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: (data.job_title ?? "").trim() || null,
      provider_name: vendorUserDetails?.provider_name,
      organization_id: hyperComplyOrgId,

      // Generate provider_subrole_dto with unique IDs
      provider_user_subroles: rolesArray.map((role) => ({
        SubroleId: uuidv4(),
        Subrole: role,
      })),

      // Generate provider_rights_dto with unique IDs
      provider_user_permissions: rights.map((right) => ({
        // PermissionId: uuidv4(),
        Permission: right,
      })),
    };

    if (userId) {
      // Update existing user
      updateProviders(
        { id: clerkId, userData: updateProviderData },
        {
          onSuccess: async () => {
            await saveDesignations(
              vendorUserDetails?.provider_user_id,
              vendorUserDetails?.provider_id,
              data.email,
            );
            // Force refetch after update succeeds
            setRefreshKey(Date.now());
            refetch();

            // Show success message with a delay to allow refetch to complete
            setTimeout(() => {
              setIsLoading(false);
              setIsSuccess(true);
              setMessage("User updated successfully!");
              setAlertOpen(true);

              // Add a longer delay before navigation to ensure data is refreshed
              setTimeout(
                () => navigate(user === "Provider User"
                  ? getVendorTeamProfilePath(vendorUserDetails?.provider_id)
                  : "/Backend/Users/Vendors?tab=users"),
                2000
              );
            }, 300);
          },
          onError: (error) => {
            setIsLoading(false);
            setIsSuccess(false);

            // Safely extract the error message
            let errorMessage = "Failed to update user";

            if (error.response?.data) {
              // Check if data is an object with message property
              if (
                typeof error.response.data === "object" &&
                error.response.data.message
              ) {
                errorMessage = error.response.data.message;
              }
              // Check if data is an object with code and message properties
              else if (
                typeof error.response.data === "object" &&
                error.response.data.code
              ) {
                errorMessage = `Error ${error.response.data.code}: ${
                  error.response.data.message || "Unknown error"
                }`;
              }
              // If it's a string, use it directly
              else if (typeof error.response.data === "string") {
                errorMessage = error.response.data;
              }
            }

            setMessage(errorMessage);
            setAlertOpen(true);
          },
        }
      );
    } else {
      // Create new user
      // COMMENTED OUT - Dual Role Logic
      // if (enableDualRole) {
      //   // Validate sponsor roles are selected
      //   if (selectedSponsorRoles.length === 0) {
      //     setIsSuccess(false);
      //     setMessage("Please select at least one sponsor role for dual role");
      //     setAlertOpen(true);
      //     setIsLoading(false);
      //     return;
      //   }

      //   const selectedProvider = watch("provider_id");

      //   // Create user in both vendor and sponsor portals
      //   addProviderUser(
      //     { providerData: providerData },
      //     {
      //       onSuccess: () => {
      //         const sponsorPermissions =
      //           getSponsorPermissionsForRoles(selectedSponsorRoles);

      //         const sponsorUserData = {
      //           email: data.email,
      //           phone_number: data.phone_number,
      //           office_phone_number: data.office_phone_number,
      //           contact_preference: data.contact_preference,
      //           first_name: data.first_name,
      //           last_name: data.last_name,
      //           sponsor_id:
      //             selectedProvider?.sponsor_id || selectedProvider?.provider_id,
      //           sponsor_name: selectedProvider?.sponsor_name || vendorName,
      //           is_active: true,
      //           hypercomply_org_id: hyperComplyOrgId,
      //           sponsor_subrole_dto: selectedSponsorRoles.map((role) => ({
      //             sponsor_subrole_id: uuidv4(),
      //             sub_role: role,
      //           })),
      //           sponsor_rights_dto: sponsorPermissions.map((right) => ({
      //             sponsor_rights_id: uuidv4(),
      //             rights: right,
      //           })),
      //         };

      //         addSponsorUser(sponsorUserData, {
      //           onSuccess: () => {
      //             setIsLoading(false);
      //             setIsSuccess(true);
      //             setMessage(
      //               "User created in both Vendor and Sponsor portals!"
      //             );
      //             setAlertOpen(true);
      //             setTimeout(
      //               () => navigate(`/Backend/Users/Vendors?tab=users`),
      //               2000
      //             );
      //           },
      //           onError: (error) => {
      //             setIsLoading(false);
      //             setIsSuccess(false);
      //             let errorMessage =
      //               "Vendor user created, but failed to create sponsor user";
      //             if (error.response?.data?.message) {
      //               errorMessage = error.response.data.message;
      //             }
      //             setMessage(errorMessage);
      //             setAlertOpen(true);
      //           },
      //         });
      //       },
      //       onError: (error) => {
      //         setIsLoading(false);
      //         setIsSuccess(false);
      //         let errorMessage = "Failed to add user";
      //         if (error.response?.data) {
      //           if (
      //             typeof error.response.data === "object" &&
      //             error.response.data.message
      //           ) {
      //             errorMessage = error.response.data.message;
      //           } else if (typeof error.response.data === "string") {
      //             errorMessage = error.response.data;
      //           }
      //         }
      //         setMessage(errorMessage);
      //         setAlertOpen(true);
      //       },
      //     }
      //   );
      // } else {
        // Single role creation (existing behavior)
        // return console.log({ providerData, selectedRole, role: data.role })
        addProviderUser(
          { providerData: providerData },
          {
            onSuccess: async () => {
              await saveDesignations(providerUserId, targetProviderId, data.email);
              setIsLoading(false);
              setIsSuccess(true);
              setMessage("User added successfully!");
              setAlertOpen(true);
              setTimeout(
                () => navigate(user === "Provider User"
                  ? getVendorTeamProfilePath(targetProviderId)
                  : "/Backend/Users/Vendors?tab=users"),
                2000
              );
            },
            onError: (error) => {
              setIsLoading(false);
              setIsSuccess(false);

              let errorMessage = "Failed to add user";

              if (error.response?.data) {
                if (
                  typeof error.response.data === "object" &&
                  error.response.data.message
                ) {
                  errorMessage = error.response.data.message;
                } else if (
                  typeof error.response.data === "object" &&
                  error.response.data.code
                ) {
                  errorMessage = `Error ${error.response.data.code}: ${
                    error.response.data.message || "Unknown error"
                  }`;
                } else if (typeof error.response.data === "string") {
                  errorMessage = error.response.data;
                }
              }

            setMessage(errorMessage);
            setAlertOpen(true);
          },
        }
      );
      // } // COMMENTED OUT - End of dual role else block
    }
  };

  // Show loading state when fetching user details
  if (userId && vendorUserDetailsLoading) {
    return (
      <div className="w-full flex justify-center px-4">
        <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg p-8">
          <p className="text-center mt-4 text-gray-600">
            <FormSkeletonLoader />
          </p>
        </div>
      </div>
    );
  }

  // Checkboxes for permissions with central management
  const PermissionCheckbox = ({ rightItem, category }) => {
    // Get the current value from the form - explicitly check for boolean true
    const currentValue = watch(rightItem) === true;

    // Determine if this right belongs to any of the selected roles
    const isRoleSpecificRight = selectedRoles.some((role) => {
      if (role === "Vendor Admin") return true; // Admin can modify all rights

      // For other roles, check if this right belongs to their category
      if (
        (role === "RFI Manager - Approver" || role === "RFI Manager - User") &&
        category === "RFI Permissions"
      )
        return true;
      if (role === "VQA Manager" && category === "Assessment Permissions")
        return true;

      return false;
    });

    // General permissions are always editable if any role is selected
    const isGeneralPermission =
      category === "General Permissions" && selectedRoles.length > 0;

    // Admin permissions are only editable by Vendor Admin
    const isAdminPermission =
      category === "Admin Permissions" &&
      selectedRoles.includes("Vendor Admin");

    // Determine if this checkbox should be editable
    const isEditable =
      !rolesLocked &&
      (isRoleSpecificRight || isGeneralPermission || isAdminPermission);

    // For debugging - add data attributes
    const dataAttributes = {
      "data-checked": currentValue.toString(),
      "data-editable": isEditable.toString(),
    };

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5 pt-1">
          <input
            type="checkbox"
            id={`right-${rightItem}`}
            checked={currentValue}
            disabled={!isEditable}
            onChange={(e) => {
              setValue(rightItem, e.target.checked, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            className={`h-4 w-4 rounded border-gray-300 ${
              isEditable
                ? "text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                : "text-gray-300 cursor-not-allowed"
            }`}
            {...dataAttributes}
          />
        </div>
        <div className={`ml-3 text-sm ${!isEditable ? "opacity-50" : ""}`}>
          <label
            htmlFor={`right-${rightItem}`}
            className={`font-medium ${
              isEditable ? "text-gray-700" : "text-gray-400"
            }`}
          >
            {RIGHTS_DISPLAY_NAMES[rightItem] || rightItem}
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {rightsDescriptions[rightItem] || "No description available"}
          </p>
        </div>
      </div>
    );
  };

  const onUserSelect = (user) => {
    if (user) {
      setValue("first_name", user.first_name || "");
      setValue("last_name", user.last_name || "");
      setValue("job_title", user.job_title || user.title || "");
      setValue("email", user.email);
      setValue("phone_number", user.phone_number || "");
      setValue("office_phone_number", user.office_phone_number || "");
      setValue(
        "contact_preference",
        user.contact_preference || "Mobile_Phone"
      );
      const defaultVendorRole = EXISTING_SPONSOR_ROLE_ID_TO_VENDOR_ROLE.admin;
      setValue("role", [defaultVendorRole]);
      syncVendorPermissionsAfterRoleChange([defaultVendorRole]);
    } else {
      setValue("first_name", "");
      setValue("last_name", "");
      setValue("job_title", "");
      setValue("email", "");
      setValue("phone_number", "");
      setValue("office_phone_number", "");
      setValue("contact_preference", "Mobile_Phone");
      setValue("role", []);
      syncVendorPermissionsAfterRoleChange([]);
    }
  };

  const onExistingSponsorUserRoleChange = (_userId, roleId) => {
    const vendorRole = EXISTING_SPONSOR_ROLE_ID_TO_VENDOR_ROLE[roleId];
    if (!vendorRole) return;
    setValue("role", [vendorRole]);
    syncVendorPermissionsAfterRoleChange([vendorRole]);
  };

  const { title: formTitle, description: formDescription } = getVendorUserFormCopy({
    isEditingSelf,
    userId,
  });

  return (
    <div className="w-full flex justify-center px-4">
      <NotificationModal
        open={alertOpen}
        setOpen={setAlertOpen}
        desc={message}
        title={isSuccess ? "Success" : "Error"}
        error={!isSuccess}
      />

      {/* Form container with half-width and centered */}
      <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full p-6">
          {/* Header */}
          <div className="pb-5 border-b border-gray-200">
            <h1 className="font-semibold text-xl">{formTitle}</h1>
            <p className="text-sm text-gray-600">{formDescription}</p>
          </div>

          {/* User Information */}
          <div className="mt-6 space-y-6">
            {userId ? (
              <div className="border-b border-gray-200 pb-6">
                <label className="block text-sm font-medium text-gray-900">
                  Vendor Organization
                </label>
                <div className="mt-1 w-full border-gray-300 rounded-md text-sm bg-gray-100 px-3 py-2">
                  {vendorUserDetails?.provider_name}
                </div>
              </div>
            ) : providerIdParam ? (
              // Arrived from a specific org (Manage Org > Team or vendor
              // self-service): the org is fixed, show it read-only (14464).
              <div className="border-b border-gray-200 pb-6">
                <label className="block text-sm font-medium text-gray-900">
                  Vendor Organization
                </label>
                <div className="mt-1 w-full border-gray-300 rounded-md text-sm bg-gray-100 px-3 py-2">
                  {providerName || vendorOrgDetails?.name || "—"}
                </div>
              </div>
            ) : (
              <div className="border-b border-gray-200 pb-6">
                <FormInput
                  label="Vendor Organization"
                  name="provider_id"
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  watch={watch}
                  type="select"
                  options={providerOrgData || []}
                  disabled={!!userId} // Provider org is readonly in edit mode
                />
              </div>
            )}

            {/* Dual Role Toggle - Only show in create mode */}
            {/* {!userId && (
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dual-role-toggle"
                    checked={enableDualRole}
                    onChange={(e) => {
                      setEnableDualRole(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedSponsorRoles([]);
                      }
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="dual-role-toggle"
                    className="ml-3 text-sm font-medium text-gray-900"
                  >
                    Enable Dual Role
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500 ml-7">
                  User will be created in both Vendor and Sponsor portals with
                  their respective permissions
                </p>
              </div>
            )} */}

            {/* Sponsor Roles Selection - Only show when dual role is enabled */}
            {/* {!userId && enableDualRole && (
              <div className="border-b border-gray-200 pb-6">
                <div className="pb-3">
                  <h2 className="font-semibold text-lg text-gray-900">
                    Sponsor Portal Roles
                  </h2>
                  <p className="text-sm text-gray-600">
                    Select the roles for the Sponsor portal
                  </p>
                </div>
                <div className="space-y-4">
                  {Object.keys(roleDefinitions).map((role) => (
                    <div
                      key={`sponsor-${role}`}
                      className="rounded-md px-2 py-1"
                    >
                      <div
                        className={`flex items-center ${
                          isSponsorRoleDisabled(role) ? "opacity-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`sponsor-role-${role}`}
                          value={role}
                          checked={selectedSponsorRoles.includes(role)}
                          onChange={(e) => handleSponsorRoleChange(e, role)}
                          disabled={isSponsorRoleDisabled(role)}
                          className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                            isSponsorRoleDisabled(role)
                              ? "cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        />
                        <label
                          htmlFor={`sponsor-role-${role}`}
                          className="ml-3 flex-1"
                        >
                          <span className="block text-sm font-medium text-gray-900">
                            {role}
                          </span>
                          <span className="block text-sm text-gray-500 mt-1">
                            {roleDefinitions[role]?.description || ""}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {selectedProvider && orgIsDualRole && availableSponsorUsers.length > 0 && (
              <ExistingUserSelector
                key={selectedProvider?.provider_id}
                type="sponsor"
                users={availableSponsorUsers}
                onUserSelect={onUserSelect}
                onRoleChange={onExistingSponsorUserRoleChange}
              />
            )}

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-6">
              <FormInput
                label="First Name"
                name="first_name"
                register={register}
                errors={errors}
                placeholder="Enter first name"
                className="col-span-1"
              />
              <FormInput
                label="Last Name"
                name="last_name"
                register={register}
                errors={errors}
                placeholder="Enter last name"
                className="col-span-1"
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Title"
                name="job_title"
                register={register}
                errors={errors}
                placeholder="e.g. VP of Clinical Operations"
              />
            </div>

            {/* Email - Readonly when editing */}
            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Email"
                name="email"
                register={register}
                errors={errors}
                type="email"
                placeholder="user@example.com"
                disabled={!!userId} // Email is readonly in edit mode
              />
            </div>

            {/* Phone Numbers */}
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-6">
              <FormInput
                label="Mobile Phone Number"
                name="phone_number"
                register={register}
                errors={errors}
                placeholder="e.g 1-234-567-8900"
                className="col-span-1"
              />
              <FormInput
                label="Office Phone Number"
                name="office_phone_number"
                register={register}
                errors={errors}
                placeholder="e.g 1-234-567-8900"
                className="col-span-1"
              />
            </div>

            {/* Contact Preference */}
            <div className="border-b border-gray-200 pb-6">
              <RadioGroup
                label="Contact Preference"
                name="contact_preference"
                register={register}
                errors={errors}
              />
            </div>

            {/* Security: read-only MFA state, edit mode only (#14476) */}
            {userId && (
              <MfaReadOnlyField
                enabled={vendorUserDetails?.two_factor_enabled}
                idSuffix="provider-user"
              />
            )}

            {/* Roles Section */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">User Roles</h2>
            </div>

            <div className="border-b border-gray-200 pb-6 space-y-4">
              {Object.keys(vendorRoleDefinitions).map((role) => (
                <div key={role} className="rounded-md px-2 py-1">
                  <div
                    className={`flex items-center ${
                      isRoleDisabled(role) ? "opacity-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`role-${role}`}
                      value={role}
                      checked={selectedRoles.includes(role)}
                      onChange={(e) => handleRoleChange(e, role)}
                      disabled={isRoleDisabled(role)}
                      className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                        isRoleDisabled(role)
                          ? "cursor-normal"
                          : "cursor-pointer"
                      }`}
                    />
                    <label htmlFor={`role-${role}`} className="ml-3 flex-1">
                      <span className="block text-sm font-medium text-gray-900">
                        {ROLE_DISPLAY_NAMES[role] || role}
                      </span>
                      {/* <span className="block text-sm text-gray-500 mt-1">
                        {roleDefinitions[role].description || ""}
                      </span> */}
                    </label>
                  </div>
                </div>
              ))}
              {errors.role && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.role.message}
                </p>
              )}
              {rolesLocked && (
                <p className="mt-2 text-xs text-gray-500">
                  You cannot change your own role. Ask another Vendor Admin or a
                  Diligent Admin.
                </p>
              )}
            </div>

            <ContactDesignationSection
              selectedRoles={selectedRoles}
              rfiDesignation={rfiContactDesignation}
              assessmentDesignation={assessmentContactDesignation}
              onRfiChange={setRfiContactDesignation}
              onAssessmentChange={setAssessmentContactDesignation}
              readOnly={contactDesignationsReadOnly}
              readOnlyReason={
                contactDesignationsReadOnly
                  ? "Your organization has only one user, so request contact designations cannot be changed."
                  : undefined
              }
            />

            {/* Permissions Section */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">User Permissions</h2>
              <p className="text-sm text-gray-600">
                Permissions will be automatically assigned based on selected
                roles.
              </p>
            </div>

            <div className="pb-6 space-y-6">
              {Object.entries(vendorRightsCategories).map(
                ([category, categoryData]) => (
                  <div key={category} className=" rounded-md">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-900">
                        {PERMISSION_CATEGORY_DISPLAY_NAMES[category] || category}
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      {categoryData.rights.map((rightItem) => (
                        <PermissionCheckbox
                          key={rightItem}
                          rightItem={rightItem}
                          category={category}
                        />
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Hidden inputs for all rights */}
            {vendorRightsList.map((right) => (
              <input
                type="hidden"
                key={`hidden-${right}`}
                {...register(right)}
              />
            ))}

            {/* Form Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate(user === "Provider User"
                  ? getVendorTeamProfilePath()
                  : "/Backend/Users/Vendors?tab=users")}
                className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-100 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUserRoleSelectionSaveDisabled(isLoading, selectedRoles)}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                  isUserRoleSelectionSaveDisabled(isLoading, selectedRoles)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-500"
                }`}
              >
                {isLoading ? "Saving..." : userId ? "Save Changes" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProviderUsers;
